import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Icon from 'tbk-components/src/components/Icon';

export interface IMainNavMobileLogo {
	image?: IImage;
	link?: ILink;
}

export interface IMainNavMobileTab {
	icon?: string;
	label?: string;
	children?: any;
	link?: ILink;
}

export interface IMainNavMobileProps extends IBaseElementProps {
	children?: any;
	logo?: IMainNavMobileLogo;
	leftTabs?: IMainNavMobileTab[];
	rightTabs?: IMainNavMobileTab[];
}

let lastFocus = null;

const MainNavMobile: React.FC<IMainNavMobileProps> = ({
	children,
	className,
	classNames = [],
	logo,
	leftTabs,
	rightTabs,
}) => {
	const [selectedTab, setSelectedTab] = useState<number | null>(null);
	const [tabSide, setTabSide] = React.useState<string>('');
	const ref = useRef<HTMLDivElement>(null);

	// Handle Escape key
	useEffect(() => {

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setSelectedTab(null);
				if (lastFocus) {
					lastFocus.focus();
				}
			}
		};

		// eslint-disable-next-line no-undef
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			// eslint-disable-next-line no-undef
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const handleDropdownHeight = (event: any) => {
			const el = ref.current.querySelector(
				'.main-nav__content',
			) as HTMLDivElement;
			el.style.height = (window.innerHeight - 50) + 'px';
		};

		if (selectedTab !== null) {
			document.body.classList.add('nav-open');
			document.body.style.top = `-${window.scrollY}px`;
			document.body.style.position = 'fixed';
			document.body.style.width = '100%';

			const dropdowns = ref.current?.querySelectorAll(
				'.nav-link.collapse-toggle',
			) as NodeListOf<HTMLDivElement>;

			dropdowns &&
				dropdowns.forEach((dropdown) => {
					dropdown.addEventListener('click', handleDropdownHeight);
			});
		} else {
			// eslint-disable-next-line no-undef
			document.documentElement.style.scrollBehavior = 'auto';
			const scrollY = document.body.style.top;
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			window.scrollTo({ top: (parseInt(scrollY || '0') * -1), behavior: 'auto' });
			document.body.classList.remove('nav-open');

			const el = ref.current.querySelector(
				'.main-nav__content',
			) as HTMLDivElement;
			el.style.height = '';
		}
	}, [selectedTab]);

	return (
		<div
			className={cn(
				...(className
					? [className]
					: ['main-nav', 'main-nav--mobile', ...classNames]),
				selectedTab !== null && 'main-nav--open',
			)}
			ref={ref}
		>
			<div className="main-nav__inner">
				{leftTabs && leftTabs.length > 0 && (
					<div className="main-nav__tabs">
						{leftTabs.map(({ icon, label, link }, index) => (
							<>
								{link && (
									<Button
										key={index}
										className={cn(
											`main-nav__tab left-tab-${index}`,
											selectedTab === index &&
												'main-nav__tab--selected',
										)}
										href={link.href}
									>
										{icon && <Icon name={icon} />}
										{label}
									</Button>
								)}
								{!link && (
									<Button
										key={index}
										className={cn(
											`main-nav__tab left-tab-${index}`,
											selectedTab === index &&
												'main-nav__tab--selected',
										)}
										onClick={() => {
											setSelectedTab(
												selectedTab === index
													? null
													: index,
											);
											setTabSide('left');
										}}
									>
										{icon && <Icon name={icon} />}
										{label}
									</Button>
								)}
							</>
						))}
					</div>
				)}
				{logo && (
					<a className="main-nav__logo" href={logo.link.href}>
						<Image {...logo.image} />
					</a>
				)}
				{rightTabs && rightTabs.length > 0 && (
					<div className="main-nav__tabs">
						{rightTabs.map(({ icon, label, link }, index) => (
							<>
								{link && (
									<Button
										key={index}
										className={cn(
											`main-nav__tab right-tab-${index}`,
											selectedTab === index &&
												'main-nav__tab--selected',
										)}
										href={link.href}
									>
										{icon && <Icon name={icon} />}
										{label}
									</Button>
								)}
								{!link && (
									<Button
										key={index}
										className={cn(
											`main-nav__tab right-tab-${index}`,
											selectedTab === index &&
												'main-nav__tab--selected',
										)}
										onClick={() => {
											setSelectedTab(
												selectedTab === index
													? null
													: index,
											);
											setTabSide('right');
										}}
									>
										{icon && <Icon name={icon} />}
										{label}
									</Button>
								)}
							</>
						))}
					</div>
				)}
			</div>
			{leftTabs && leftTabs.length > 0 && (
				<div
					className={`main-nav__content main-nav__content--${tabSide}`}
					tabIndex={-1}
					ref={ref}
				>
					<button
						type="button"
						className="btn-close"
						aria-label="Close Menu"
						onClick={() => {
							setSelectedTab(null);
							setTabSide('');
						}}
					>
						<Icon name="close" />
					</button>
					{tabSide === 'left'
						? selectedTab !== null && leftTabs[selectedTab].children
						: selectedTab !== null &&
						  rightTabs[selectedTab].children}
				</div>
			)}
		</div>
	);
};

export default MainNavMobile;
